import { Component, computed, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { CdkDrag, CdkDropList, type CdkDragDrop } from '@angular/cdk/drag-drop';
import { BrnAccordionContent } from '@spartan-ng/brain/accordion';
import { HlmAccordionImports } from '@mb/ui/accordion';
import { HlmDropdownMenuImports } from '@mb/ui/dropdown-menu';
import { NgDiagramViewportService } from 'ng-diagram';
import type { PositionAssigneeDto } from '../../../shared';
import {
  MAX_VISIBLE_PERSON_ROWS,
  PERSON_ROW_HEIGHT,
  type NestedPositionData,
} from './org-chart.model';
import { PersonRowComponent } from './person-row.component';
import {
  OrgChartStore,
  orgChartDropListId,
  positionIdFromDropList,
} from './org-chart.state';

/**
 * One position (Stelle) as a row inside its OE card.
 *
 * The header is the accordion trigger that opens the persons underneath it,
 * indented on a guide line. The actions sit over the header's right end and
 * only show on hover or focus, so the row stays one line at
 * `POSITION_ROW_HEIGHT` — beside the trigger rather than inside it, because a
 * button may not hold buttons.
 *
 * The whole row is the drop target, so a person can be moved onto a position
 * whose list is closed.
 */
@Component({
  selector: 'org-oe-position-row',
  standalone: true,
  imports: [
    CdkDropList,
    CdkDrag,
    PersonRowComponent,
    TranslocoPipe,
    HlmAccordionImports,
    HlmDropdownMenuImports,
    BrnAccordionContent,
  ],
  templateUrl: './oe-position-row.component.html',
  host: { class: 'block' },
})
export class OePositionRowComponent {
  readonly store = inject(OrgChartStore);
  readonly dragScale = inject(NgDiagramViewportService).scale;
  readonly position = input.required<NestedPositionData>();
  /** Left accent bar, in the colour of the OE the row sits in. */
  readonly accentClass = input('border-l-slate-300');

  readonly dropListId = computed(() =>
    orgChartDropListId(this.position().positionId),
  );
  readonly expanded = computed(() =>
    this.store.expandedPositionIdSet().has(this.position().positionId),
  );
  readonly listMaxHeight = MAX_VISIBLE_PERSON_ROWS * PERSON_ROW_HEIGHT;

  readonly canDelete = computed(() => this.position().canDelete);
  /** The ⋮ menu goes when it would open onto nothing. */
  readonly hasMenu = computed(() => this.canDelete());
  readonly hasActions = computed(
    () => this.position().canManage || this.hasMenu(),
  );

  onExpandedChange(expanded: boolean): void {
    this.store.setExpanded(this.position().positionId, expanded);
  }

  onDrop(event: CdkDragDrop<PositionAssigneeDto[]>): void {
    if (!this.position().canManage) return;
    if (event.previousContainer === event.container) return;
    const assignee = event.item.data as PositionAssigneeDto;
    const toPositionId = positionIdFromDropList(event.container.id);
    void this.store.move({
      personPositionId: assignee.personPositionId,
      toPositionId,
    });
  }

  onViewPermissions(event: Event): void {
    if (!this.position().canManage) return;
    event.stopPropagation();
    this.store.viewPermissions({
      kind: 'position',
      id: this.position().positionId,
    });
  }

  onEdit(event: Event): void {
    if (!this.position().canManage) return;
    event.stopPropagation();
    this.store.edit({
      kind: 'position',
      id: this.position().positionId,
    });
  }

  onAddPerson(event: Event): void {
    if (!this.position().canManage) return;
    event.stopPropagation();
    this.store.create({
      kind: 'person',
      contextId: this.position().positionId,
    });
  }

  onDelete(): void {
    if (!this.canDelete()) return;
    this.store.remove({
      kind: 'position',
      id: this.position().positionId,
    });
  }
}
